import React from 'react';
import { InputSwitch } from 'primereact/inputswitch';
import { toast } from 'react-toastify';
import AnalysisSummaryCard from './tables/AnalysisSummaryCard';
import PatternTable from './tables/PatternTable';
import PatternPriceTable from './tables/PatternPriceTable';
import TypologyTable from './tables/TypologyTable';
import TypologyOfferTable from './tables/TypologyOfferTable';
import VGVTable from './tables/VGVTable';

const filterHorizontal = (metaData, useCasas = false) => {
    if (useCasas) {
        return metaData.filter(
            (item) =>
                item.padrao !== 'Loteamento Fechado' &&
                item.padrao !== 'Loteamento Aberto' &&
                item.padrao !== 'Condomínio de Casas/Sobrados'
        );
    }
    return metaData.filter(
        (item) => item.padrao === 'Loteamento Fechado' || item.padrao === 'Loteamento Aberto'
    );
};

// Exportação XLS/PDF da área (paridade com o InfoCard do mapa Google): GET para a URL do
// snapshot com `nome={cache_key}` + toggle "Detalhar empreendimentos" (`accept{cache_key}`),
// montada no clique. InputSwitch (PrimeReact) como no resto do mapa — o tema global
// (light-bootstrap-dashboard.css) esconde checkboxes nativos dentro de .form-check.
// Só aparece com a permissão ver-estoque-excel-mapa.
const ExportXlsForm = ({ metaData }) => {
    const [detalhar, setDetalhar] = React.useState(false);
    const [downloadingXls, setDownloadingXls] = React.useState(false);
    const [downloadingPdf, setDownloadingPdf] = React.useState(false);

    if (!window.verEstoqueExcelMapa || !metaData?.file?.excel) return null;

    const switchId = `accept${metaData.cache_key}`;
    const downloading = downloadingXls || downloadingPdf;

    // fetch + blob (como o download de PDF do EmpreendimentoModal): permite exibir o
    // indicador enquanto o backend gera o arquivo — navegação direta não dá sinal
    // de início/fim do download.
    const baixarArquivo = async (baseUrl, filename, rotulo, setLoading) => {
        const params = new URLSearchParams({ nome: metaData.cache_key });
        if (detalhar) params.set(switchId, 'true');

        setLoading(true);
        try {
            const response = await fetch(`${baseUrl}?${params.toString()}`);

            if (response.status === 404) {
                toast.warn('Exportação expirada. Refaça a busca no mapa.');
                return;
            }
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error(`[MapboxResultsPanel] erro ao baixar ${rotulo}:`, e);
            toast.error(`Erro ao gerar o arquivo ${rotulo}.`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mb-3 px-1 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
                <InputSwitch
                    inputId={switchId}
                    checked={detalhar}
                    onChange={(e) => setDetalhar(e.value)}
                    disabled={downloading}
                />
                <label htmlFor={switchId} className="pl-1 mb-0 ms-2 small">
                    Detalhar empreendimentos
                </label>
            </div>
            <div className="d-flex align-items-center">
                {metaData.file.pdf && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger mr-2"
                        onClick={() => baixarArquivo(metaData.file.pdf, 'relatorio_mapa.pdf', 'PDF', setDownloadingPdf)}
                        disabled={downloading}
                        title={downloadingPdf ? 'Gerando arquivo…' : 'Exportar PDF'}
                    >
                        {downloadingPdf
                            ? <><i className="fa fa-spin fa-spinner mr-1"></i> Gerando…</>
                            : <><i className="fas fa-file-pdf mr-1"></i> PDF</>}
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={() => baixarArquivo(metaData.file.excel, 'relatorio_areas_selecionadas.xlsx', 'XLS', setDownloadingXls)}
                    disabled={downloading}
                    title={downloadingXls ? 'Gerando arquivo…' : 'Exportar XLS'}
                >
                    {downloadingXls
                        ? <><i className="fa fa-spin fa-spinner mr-1"></i> Gerando…</>
                        : <><i className="fas fa-file-excel mr-1"></i> XLS</>}
                </button>
            </div>
        </div>
    );
};

const AreaSection = ({ area, onRemove }) => {
    const { meta_data, color, name, loading, id } = area;

    if (loading) {
        return (
            <div className="text-center py-4">
                <img alt="carregando..." src="/img/loader.gif" style={{ width: 40 }} />
                <p className="text-muted small mt-2">Carregando análise...</p>
            </div>
        );
    }

    if (!meta_data || meta_data.total_empreendimentos === 0) {
        return (
            <div className="text-center py-3">
                {onRemove && (
                    <button className="mapbox-area-remove-btn" onClick={() => onRemove(id)} title="Remover">
                        <i className="fas fa-times"></i>
                    </button>
                )}
                <p className="text-muted small">Nenhum empreendimento encontrado.</p>
            </div>
        );
    }

    const tipos = meta_data.tipos_selecionados || [];

    return (
        <div className="mb-4">
            <AnalysisSummaryCard
                color={color}
                name={name}
                onRemove={onRemove ? () => onRemove(id) : null}
                totalEmpreendimentos={meta_data.total_empreendimentos}
                ofertaLancada={meta_data.oferta_lancada}
                ofertaFinal={meta_data.oferta_final}
                unidadesVendidas={meta_data.total_unidades_vendidas}
                valorMedioUnidades={meta_data.valor_medio_unidades}
                valorMedioMetroQuadrado={meta_data.valor_medio_metro_quadrado}
            />

            <ExportXlsForm metaData={meta_data} />

            {tipos.includes('Vertical') && meta_data.infos_padrao?.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <PatternTable padroes={meta_data.infos_padrao} color={color} title="VERTICAL" />
                    <PatternPriceTable padroes={meta_data.precos_padrao} color={color} title="VERTICAL" />
                    {meta_data.infos_quartos?.length > 0 && (
                        <TypologyTable tipologias={meta_data.infos_quartos} color={color} />
                    )}
                    {meta_data.infos_quartos_oferta?.unidades?.length > 0 && (
                        <TypologyOfferTable tipologias={meta_data.infos_quartos_oferta} color={color} title="VERTICAL" />
                    )}
                    <VGVTable padroes={meta_data.infos_padrao} color={color} title="VERTICAL" type="OFERTA FINAL" />
                    <VGVTable padroes={meta_data.infos_padrao} color={color} title="VERTICAL" type="Lançado" />
                </div>
            )}

            {tipos.includes('Horizontal') && meta_data.infos_padrao_horizontal?.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    {filterHorizontal(meta_data.infos_padrao_horizontal).length > 0 && (<>
                        <PatternTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal)}
                            color={color}
                            title="HORIZONTAL"
                        />
                        <PatternPriceTable
                            padroes={filterHorizontal(meta_data.precos_padrao_horizontal)}
                            color={color}
                            title="HORIZONTAL"
                        />
                        <VGVTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal)}
                            color={color}
                            title="HORIZONTAL"
                            type="DE OFERTA FINAL"
                        />
                        <VGVTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal)}
                            color={color}
                            title="HORIZONTAL"
                            type="Lançado"
                        />
                    </>)}

                    {filterHorizontal(meta_data.infos_padrao_horizontal, true).length > 0 && (<>
                        <PatternTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal, true)}
                            color={color}
                            title="HORIZONTAL - CONDOMÍNIO DE CASAS/SOBRADOS"
                        />
                        <PatternPriceTable
                            padroes={filterHorizontal(meta_data.precos_padrao_horizontal, true)}
                            color={color}
                            title="HORIZONTAL - CONDOMÍNIO DE CASAS/SOBRADOS"
                        />
                        <VGVTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal, true)}
                            color={color}
                            title="HORIZONTAL - CONDOMÍNIO DE CASAS/SOBRADOS"
                            type="de Oferta Final"
                        />
                        <VGVTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal, true)}
                            color={color}
                            title="HORIZONTAL - CONDOMÍNIO DE CASAS/SOBRADOS"
                            type="Lançado"
                        />
                    </>)}
                </div>
            )}

            {tipos.includes('Comercial') && meta_data.infos_padrao_comercial?.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <PatternTable padroes={meta_data.infos_padrao_comercial} color={color} title="COMERCIAL" />
                    <PatternPriceTable padroes={meta_data.precos_padrao_comercial} color={color} title="COMERCIAL" />
                    <VGVTable padroes={meta_data.infos_padrao_comercial} color={color} title="COMERCIAL" type="de oferta final" />
                    <VGVTable padroes={meta_data.infos_padrao_comercial} color={color} title="COMERCIAL" type="Lançado" />
                </div>
            )}

            <hr className="hr-mapa" />
        </div>
    );
};

const MapboxResultsPanel = ({ searchAreas, onRemoveArea }) => {
    if (!searchAreas || searchAreas.length === 0) {
        return (
            <p className="text-muted small text-center mt-4">
                Faça uma busca para visualizar os resultados de análise.
            </p>
        );
    }

    return (
        <div>
            {searchAreas.map((area) => (
                <AreaSection key={area.id} area={area} onRemove={onRemoveArea} />
            ))}
        </div>
    );
};

export default MapboxResultsPanel;
